import type { ShopifyGraphqlExecutor } from '@repo/shopify/client';
import { addToCart, createCart } from '@repo/shopify/mutations/cart';
import type { Cart } from '@repo/shopify/types';
import {
	createShopifyRequestContext,
	createStorefrontClient,
	type I18nConfig,
	type StorefrontQueryString,
} from '@shopify/hydrogen';

export type BrowserCartTransportOptions = {
	cartId?: string;
	lines: Parameters<typeof addToCart>[1];
	config: { storeDomain: string; storefrontId: string; publicToken: string };
	trackingValues: { uniqueToken: string; visitToken: string; consent: string };
	pageUrl: string;
	i18n: I18nConfig;
	signal?: AbortSignal;
	fetch?: typeof fetch;
};

const CART_REQUEST_ERROR = 'The cart update could not be confirmed. Please refresh your cart.';

/** One actual cart mutation through the shopper's browser; never retries or falls back. */
export async function addBrowserCartLines(options: BrowserCartTransportOptions): Promise<Cart> {
	try {
		const { config, trackingValues, signal, i18n } = options;
		if (
			!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(config.storeDomain) ||
			!/^\d+$/.test(config.storefrontId) ||
			!config.publicToken.trim() ||
			!trackingValues.uniqueToken ||
			!trackingValues.visitToken ||
			!trackingValues.consent ||
			!i18n.country ||
			!i18n.language ||
			options.lines.length === 0 ||
			(options.cartId !== undefined && !options.cartId.startsWith('gid://shopify/Cart/'))
		)
			throw new Error(CART_REQUEST_ERROR);
		signal?.throwIfAborted();
		const apiUrl = `https://${config.storeDomain}/api/2026-04/graphql.json`;
		const nativeFetch = options.fetch ?? globalThis.fetch;
		const requestContext = createShopifyRequestContext({
			request: {
				url: options.pageUrl,
				signal,
				headers: new Headers({
					'X-Shopify-UniqueToken': trackingValues.uniqueToken,
					'X-Shopify-VisitToken': trackingValues.visitToken,
				}),
			},
			i18n,
		});
		const client = createStorefrontClient({
			type: 'public',
			requestContext,
			config: {
				storeDomain: config.storeDomain,
				storefrontId: config.storefrontId,
				publicStorefrontToken: config.publicToken,
				apiVersion: '2026-04',
				defaultTimeoutInMs: 10_000,
				fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
					if (String(input) !== apiUrl) throw new Error(CART_REQUEST_ERROR);
					const headers = new Headers(init?.headers);
					// Keep the successful browser control's CORS contract. The browser
					// supplies its own network identity; HttpOnly cookies stay first-party.
					for (const name of [...headers.keys()]) {
						if (
							name.startsWith('sec-') ||
							[
								'cookie',
								'authorization',
								'shopify-storefront-private-token',
								'shopify-storefront-buyer-ip',
								'origin',
								'referer',
								'user-agent',
								'x-hydrogen-version',
							].includes(name)
						)
							headers.delete(name);
					}
					return nativeFetch(input, {
						...init,
						headers,
						cache: 'no-store',
						credentials: 'omit',
						mode: 'cors',
						redirect: 'error',
					});
				},
			},
		});
		const executeGraphql: ShopifyGraphqlExecutor = async <T>(operation: {
			query: string;
			variables?: object;
		}): Promise<T> => {
			// Keep the existing rich cart document unchanged. Cart country comes
			// from buyerIdentity; no undeclared context variables are introduced.
			const result = await client.graphql(operation.query as StorefrontQueryString<T, object>, {
				variables: operation.variables,
			});
			if (result.errors?.length || !result.data) throw new Error(CART_REQUEST_ERROR);
			return result.data;
		};
		const cart = options.cartId
			? await addToCart(options.cartId, options.lines, { executeGraphql })
			: await createCart(options.lines, {
					executeGraphql,
					buyerIdentity: { countryCode: i18n.country },
				});
		if (!cart?.id || (options.cartId && cart.id !== options.cartId))
			throw new Error(CART_REQUEST_ERROR);
		return cart;
	} catch {
		// SDK errors can include GraphQL variables, cart capabilities or response
		// bodies. The caller gets only a safe message, without the upstream cause.
		throw new Error(CART_REQUEST_ERROR);
	}
}
